/**
 * Custom Hooks
 * Reusable React hooks following best practices
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { logger, CookieManager } from '../utils';
import type { LoadingState } from '../types';

// Export error reporting hook
export { useErrorReporting } from './useErrorReporting';

// Export theme hooks
export { useSafeThemeClasses } from './useTheme';

// === useAsync Hook ===
export function useAsync<T, P extends readonly unknown[] = []>(
  asyncFunction: (...args: P) => Promise<T>,
  immediate = false,
  immediateArgs?: P
) {
  const [state, setState] = useState<LoadingState & { data?: T }>({
    isLoading: immediate,
    error: null,
    success: false,
    data: undefined,
  });

  const execute = useCallback(async (...args: P) => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));
    
    try {
      const data = await asyncFunction(...args);
      setState({ isLoading: false, error: null, success: true, data });
      return data;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An error occurred';
      setState({ isLoading: false, error: errorMessage, success: false, data: undefined });
      throw error;
    }
  }, [asyncFunction]);

  useEffect(() => {
    if (immediate && immediateArgs) {
      execute(...immediateArgs);
    }
  }, [execute, immediate, immediateArgs]);

  return { ...state, execute };
}

// === useDebounce Hook ===
export function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

// === useCookieStorage Hook ===
export function useCookieStorage<T>(key: string, initialValue: T) {
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      const item = CookieManager.get(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      logger.error('Error reading from cookies', 'useCookieStorage', { key, error });
      return initialValue;
    }
  });

  const setValue = useCallback((value: T | ((val: T) => T)) => {
    try {
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      setStoredValue(valueToStore);
      CookieManager.set(key, JSON.stringify(valueToStore), {
        expires: 30, // 30 days default
        path: '/',
        secure: typeof window !== 'undefined' && window.location.protocol === 'https:',
        sameSite: 'lax'
      });
    } catch (error) {
      logger.error('Error setting cookie', 'useCookieStorage', { key, error });
    }
  }, [key, storedValue]);

  const removeValue = useCallback(() => {
    try {
      CookieManager.remove(key, { path: '/' });
      setStoredValue(initialValue);
    } catch (error) {
      logger.error('Error removing cookie', 'useCookieStorage', { key, error });
    }
  }, [key, initialValue]);

  return [storedValue, setValue, removeValue] as const;
}

// Legacy alias for backward compatibility
export const useLocalStorage = useCookieStorage;

// === useOnClickOutside Hook ===
export function useOnClickOutside<T extends HTMLElement = HTMLElement>(
  handler: () => void
) {
  const ref = useRef<T>(null);

  useEffect(() => {
    const listener = (event: MouseEvent | TouchEvent) => {
      if (!ref.current || ref.current.contains(event.target as Node)) {
        return;
      }
      handler();
    };

    document.addEventListener('mousedown', listener);
    document.addEventListener('touchstart', listener);

    return () => {
      document.removeEventListener('mousedown', listener);
      document.removeEventListener('touchstart', listener);
    };
  }, [handler]);

  return ref;
}

// === useWindowSize Hook ===
export function useWindowSize() {
  const [windowSize, setWindowSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0,
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return windowSize;
}

// === useIntersectionObserver Hook ===
export function useIntersectionObserver(
  options: IntersectionObserverInit = {}
) {
  const [isVisible, setIsVisible] = useState(false);
  const [entry, setEntry] = useState<IntersectionObserverEntry>();
  const elementRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
        setEntry(entry);
      },
      options
    );

    observer.observe(element);

    return () => {
      observer.unobserve(element);
    };
  }, [options]);

  return { isVisible, entry, elementRef };
}

// === usePrevious Hook ===
export function usePrevious<T>(value: T): T | undefined {
  const ref = useRef<T | undefined>(undefined);
  
  useEffect(() => {
    ref.current = value;
  }, [value]);
  
  return ref.current;
}

// === useToggle Hook ===
export function useToggle(initialValue = false) {
  const [value, setValue] = useState(initialValue);

  const toggle = useCallback(() => setValue(v => !v), []);
  const setTrue = useCallback(() => setValue(true), []);
  const setFalse = useCallback(() => setValue(false), []);

  return [value, { toggle, setTrue, setFalse }] as const;
}

// === useCounter Hook ===
export function useCounter(initialValue = 0) {
  const [count, setCount] = useState(initialValue);

  const increment = useCallback(() => setCount(c => c + 1), []);
  const decrement = useCallback(() => setCount(c => c - 1), []);
  const reset = useCallback(() => setCount(initialValue), [initialValue]);
  const set = useCallback((value: number) => setCount(value), []);

  return [count, { increment, decrement, reset, set }] as const;
}

// === useMediaQuery Hook ===
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia(query);
    const handler = (event: MediaQueryListEvent) => setMatches(event.matches);
    
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, [query]);

  return matches;
}

// === useTimeout Hook ===
export function useTimeout(callback: () => void, delay: number | null) {
  const savedCallback = useRef(callback);

  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  useEffect(() => {
    if (delay === null) return;

    const id = setTimeout(() => savedCallback.current(), delay);
    return () => clearTimeout(id);
  }, [delay]);
}

// === useInterval Hook ===
export function useInterval(callback: () => void, delay: number | null) {
  const savedCallback = useRef(callback);

  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  useEffect(() => {
    if (delay === null) return;

    const id = setInterval(() => savedCallback.current(), delay);
    return () => clearInterval(id);
  }, [delay]);
}
